import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useLocation } from "react-router-dom";
import { Helmet } from "react-helmet";
import {
  Autocomplete,
  Box,
  Button,
  FormControl,
  InputLabel,
  MenuItem,
  Select,
  TextField,
} from "@mui/material";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import { DatePicker } from "@mui/x-date-pickers/DatePicker";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import dayjs from "dayjs";
import { toast } from "react-toastify";
import {
  webIntegrationAxios,
  setWebIntegrationAuthSnapshot,
} from "./webIntegrationAxios";
import "./WebsiteIntegration.scss";

const REACT_APP_BACKEND_ROUTE_URL_WEB_INTEGRATION =
  process.env.REACT_APP_BACKEND_ROUTE_URL_WEB_INTEGRATION;

const initialForm = {
  name: "",
  phone: "",
  email: "",
  address: "",
  district: "",
  state: "",
  pincode: "",
  dob: "",
  gender: "",
};

function getMessage(err) {
  return (
    err?.response?.data?.message ||
    err?.message ||
    "Something went wrong. Please try again."
  );
}

/** Empty or whitespace-only strings become null for optional registration fields. */
function trimOrNull(value) {
  const s = String(value ?? "").trim();
  return s === "" ? null : s;
}

function to24Hour(time12h) {
  const match = String(time12h || "")
    .trim()
    .match(/^(\d{1,2}):(\d{2})\s*(AM|PM)$/i);
  if (!match) return "";
  let hour = Number(match[1]);
  const minute = match[2];
  const suffix = match[3].toUpperCase();
  if (suffix === "AM" && hour === 12) hour = 0;
  if (suffix === "PM" && hour !== 12) hour += 12;
  return `${String(hour).padStart(2, "0")}:${minute}`;
}

function getLocationText(location) {
  const parts = [location?.city, location?.district, location?.state].filter(Boolean);
  return parts.join(", ");
}

function extractFreeSlots(doctorData) {
  if (!doctorData) return [];
  const shiftKeys = ["first_shift", "second_shift", "third_shift"];
  const slots = [];

  shiftKeys.forEach((shiftKey) => {
    const ranges = doctorData[shiftKey] || [];
    ranges.forEach((range) => {
      (range.slots || []).forEach((slot) => {
        if (slot.status === "free") {
          slots.push({
            ...slot,
            shift: shiftKey.replace("_shift", ""),
          });
        }
      });
    });
  });

  return slots;
}

function WebsiteIntegrationInner() {
  const location = useLocation();
  const [step, setStep] = useState("loading");
  const [blocking, setBlocking] = useState(true);
  const [invalidReason, setInvalidReason] = useState("");
  const [orgCode, setOrgCode] = useState("");
  const [appMode, setAppMode] = useState("");
  const [facilityId, setFacilityId] = useState("");
  const [logoSrc, setLogoSrc] = useState("");
  const [brandingConfig, setBrandingConfig] = useState({});
  const [form, setForm] = useState(initialForm);
  const [patientRefId, setPatientRefId] = useState("");
  const [otpDigits, setOtpDigits] = useState(["", "", "", "", "", ""]);
  const otpInputRefs = useRef([]);
  const [resendBusy, setResendBusy] = useState(false);
  const [patientProfile, setPatientProfile] = useState(null);
  const [newPatientFlag, setNewPatientFlag] = useState(false);

  const [departments, setDepartments] = useState([]);
  const [departmentLoading, setDepartmentLoading] = useState(false);
  const [selectedDepartment, setSelectedDepartment] = useState(null);
  const [doctors, setDoctors] = useState([]);
  const [doctorsLoading, setDoctorsLoading] = useState(false);
  const [selectedDoctor, setSelectedDoctor] = useState(null);
  const [selectedDate, setSelectedDate] = useState(dayjs());
  const [slots, setSlots] = useState([]);
  const [slotsLoading, setSlotsLoading] = useState(false);
  const [selectedSlot, setSelectedSlot] = useState(null);
  const [noSlotsText, setNoSlotsText] = useState("");
  const [appointmentSummary, setAppointmentSummary] = useState(null);

  const urlToken = useMemo(() => {
    const q = new URLSearchParams(location.search);
    return q.get("token") || "";
  }, [location.search]);

  const isBookingStage =
    step === "appointments" || step === "confirm" || step === "booked";

  const pageTitle = isBookingStage ? "Book Appointment" : "Patient Information";

  const brandingPageStyle = useMemo(() => {
    const fontColor = brandingConfig?.font_color || "#1a2744";
    const buttonBgColor = brandingConfig?.button_bg_color || "#1976d2";
    const hoverButtonBgColor =
      brandingConfig?.hover_button_bg_color || "#1565c0";
    const buttonTextColor = brandingConfig?.button_text_color || "#ffffff";
    const rawFontStyle = String(brandingConfig?.font_style || "").trim();
    const normalizedFontFamily = rawFontStyle
      ? rawFontStyle.replace(/\bBold\b/gi, "").trim()
      : "";

    return {
      "--wi-font-color": fontColor,
      "--wi-button-bg-color": buttonBgColor,
      "--wi-button-hover-bg-color": hoverButtonBgColor,
      "--wi-button-text-color": buttonTextColor,
      ...(normalizedFontFamily ? { fontFamily: normalizedFontFamily } : {}),
    };
  }, [brandingConfig]);

  const containedButtonSx = useMemo(
    () => ({
      backgroundColor: "var(--wi-button-bg-color)",
      color: "var(--wi-button-text-color)",
      "&:hover": {
        backgroundColor: "var(--wi-button-hover-bg-color)",
      },
    }),
    []
  );

  const syncOrgHeaders = useCallback((code, mode) => {
    setWebIntegrationAuthSnapshot({ orgCode: code, appMode: mode });
  }, []);

  const resetBookingState = useCallback(() => {
    setSelectedDepartment(null);
    setDoctors([]);
    setDoctorsLoading(false);
    setSelectedDoctor(null);
    setSelectedDate(dayjs());
    setSlots([]);
    setSlotsLoading(false);
    setSelectedSlot(null);
    setNoSlotsText("");
    setAppointmentSummary(null);
  }, []);

  const fetchDepartments = useCallback(async () => {
    if (!facilityId) return;
    setDepartmentLoading(true);
    try {
      const res = await webIntegrationAxios.get("get_departments/");
      if (res.data?.status !== "Success") {
        throw new Error(res.data?.message || "Failed to fetch departments.");
      }
      setDepartments(res.data?.data || []);
    } catch (e) {
      toast.error(getMessage(e));
      setDepartments([]);
    } finally {
      setDepartmentLoading(false);
    }
  }, [facilityId]);

  const fetchSlotsForDoctor = useCallback(async (doctorId, dateValue) => {
    if (!doctorId || !dateValue) return;
    setSlotsLoading(true);
    setSelectedSlot(null);
    setNoSlotsText("");

    try {
      const res = await webIntegrationAxios.post("get_appointment_slots/", {
        doctor_id: doctorId,
        appointment_date: dateValue.format("YYYY-MM-DD"),
      });

      if (res.data?.status !== "Success") {
        throw new Error(res.data?.message || "Could not load appointment slots.");
      }

      const doctorFromSlots =
        (res.data?.data?.doctors || []).find((d) => d.doctor_id === doctorId) ||
        (res.data?.data?.doctors || [])[0];

      if (!doctorFromSlots) {
        setSlots([]);
        setNoSlotsText("No slots are available for the selected date.");
        return;
      }

      const freeSlots = extractFreeSlots(doctorFromSlots);
      setSlots(freeSlots);
      if (freeSlots.length === 0) {
        setNoSlotsText("No slots are available for the selected date.");
      }
    } catch (e) {
      setSlots([]);
      setNoSlotsText("Could not load slots for the selected date.");
      toast.error(getMessage(e));
    } finally {
      setSlotsLoading(false);
    }
  }, []);

  const runBootstrap = useCallback(async () => {
    if (!urlToken) {
      setInvalidReason(
        "Missing token in URL. Please use the link provided by your organization."
      );
      setStep("invalid");
      setBlocking(false);
      return;
    }

    setBlocking(true);
    setStep("loading");

    try {
      setWebIntegrationAuthSnapshot({ orgCode: "", appMode: "" });

      const valRes = await webIntegrationAxios.post(`organization_validation/`, {
        token: urlToken,
      });

      if (valRes.data?.status !== "Success" || !valRes.data?.data) {
        throw new Error(valRes.data?.message || "Organization validation failed.");
      }

      const vd = valRes.data.data || {};
      const oc = vd.org_code ?? vd.orgCode;
      const am = vd.app_mode ?? vd.appMode;
      if (!oc || !am) {
        throw new Error("Invalid organization validation response.");
      }
      setOrgCode(oc);
      setAppMode(am);
      syncOrgHeaders(oc, am);

      const infoRes = await webIntegrationAxios.get(`get_organization_info/`);

      if (infoRes.data?.status !== "Success") {
        throw new Error(
          infoRes.data?.message || "Could not load organization info."
        );
      }

      const infoPayload = infoRes.data || {};
      const infoData = infoPayload.data || {};
      const logo =
        infoData.collateral_image ??
        infoData.logo ??
        infoPayload.collateral_image ??
        infoPayload.logo;
      const fid = infoPayload.facility_id ?? infoData.facility_id;
      const liveBrandingConfig = infoData.branding_configuration || {};
      setFacilityId(fid || "");
      setBrandingConfig(liveBrandingConfig);

      if (logo) {
        setLogoSrc(`data:image/png;base64,${logo}`);
      } else {
        setLogoSrc("");
      }

      setStep("registration");
    } catch (e) {
      console.error(e);
      setInvalidReason(getMessage(e));
      setStep("invalid");
      toast.error(getMessage(e));
    } finally {
      setBlocking(false);
    }
  }, [urlToken, syncOrgHeaders]);

  useEffect(() => {
    runBootstrap();
  }, [runBootstrap]);

  useEffect(() => {
    return () => {
      setWebIntegrationAuthSnapshot({
        orgCode: "",
        appMode: "",
        accessToken: "",
        refreshToken: "",
      });
    };
  }, []);

  const updateField = (key, val) => {
    setForm((f) => ({ ...f, [key]: val }));
  };

  /** API expects country code + 10-digit mobile (e.g. 918742835125) */
  const buildPhonePayload = () => {
    const digits = form.phone.replace(/\D/g, "");
    return `91${digits}`;
  };

  const validateForm = () => {
    if (!String(form.name || "").trim()) {
      toast.error("Please enter your full name.");
      return false;
    }
    const phoneDigits = form.phone.replace(/\D/g, "");
    if (phoneDigits.length !== 10) {
      toast.error("Please enter a valid 10-digit mobile number.");
      return false;
    }
    if (!String(form.dob || "").trim()) {
      toast.error("Please enter your date of birth.");
      return false;
    }
    return true;
  };

  const submitRegistration = async () => {
    if (!validateForm()) return;
    if (!facilityId) {
      toast.error("Facility information is missing. Please refresh the page.");
      return;
    }

    setBlocking(true);
    try {
      const payload = {
        facility_id: facilityId,
        name: form.name.trim(),
        phone: buildPhonePayload(),
        email: trimOrNull(form.email),
        address: trimOrNull(form.address),
        district: trimOrNull(form.district),
        state: trimOrNull(form.state),
        pincode: trimOrNull(form.pincode),
        dob: form.dob,
        gender: form.gender ? form.gender : null,
      };

      const regRes = await webIntegrationAxios.post(
        `patient_guest_registration/`,
        payload
      );

      if (regRes.data?.status !== "Success") {
        throw new Error(regRes.data?.message || "Registration failed.");
      }

      const refId = regRes.data?.patient_ref_id;
      if (!refId) {
        throw new Error(
          "Registration response did not include patient reference."
        );
      }

      setPatientRefId(refId);
      // Backend sample expects `new_patient: false`; derive directly from already_registered.
      setNewPatientFlag(Boolean(regRes.data?.already_registered));

      await webIntegrationAxios.post(`send_mobile_otp/`, {
        patient_ref_id: refId,
      });

      setOtpDigits(["", "", "", "", "", ""]);
      setStep("otp");
      toast.success(regRes.data?.message || "OTP sent to your mobile.");
    } catch (e) {
      console.error(e);
      toast.error(getMessage(e));
    } finally {
      setBlocking(false);
    }
  };

  const otpString = otpDigits.join("");

  const verifyOtp = async () => {
    if (otpString.length < 4) {
      toast.error("Please enter the OTP.");
      return;
    }

    setBlocking(true);
    try {
      const res = await webIntegrationAxios.post(`verify_mobile_otp/`, {
        patient_ref_id: patientRefId,
        otp: otpString,
      });

      if (res.data?.status !== "Success") {
        throw new Error(res.data?.message || "Verification failed.");
      }

      const access = res.data?.access;
      const refresh = res.data?.refresh;
      if (access) {
        setWebIntegrationAuthSnapshot({
          orgCode,
          appMode,
          accessToken: access,
          refreshToken: refresh || "",
        });
      }

      setPatientProfile(res.data?.patient || null);
      resetBookingState();
      await fetchDepartments();
      setStep("appointments");
      toast.success(res.data?.message || "Verified successfully.");
    } catch (e) {
      console.error(e);
      toast.error(getMessage(e));
    } finally {
      setBlocking(false);
    }
  };

  const resendOtp = async () => {
    if (!patientRefId) return;
    setResendBusy(true);
    try {
      await webIntegrationAxios.post(`send_mobile_otp/`, {
        patient_ref_id: patientRefId,
      });
      toast.success("OTP resent.");
    } catch (e) {
      toast.error(getMessage(e));
    } finally {
      setResendBusy(false);
    }
  };

  const onDepartmentChange = async (_, value) => {
    setSelectedDepartment(value);
    setDoctors([]);
    setSelectedDoctor(null);
    setSlots([]);
    setSelectedSlot(null);
    setNoSlotsText("");

    if (!value?.department_name || !facilityId) return;

    setDoctorsLoading(true);
    try {
      const res = await webIntegrationAxios.post("get_doctors_by_department/", {
        // facility_id: facilityId,
        department_name: value.department_name,
      });

      if (res.data?.status !== "Success") {
        throw new Error(res.data?.message || "Failed to fetch doctors.");
      }

      setDoctors(res.data?.data || []);
    } catch (e) {
      toast.error(getMessage(e));
      setDoctors([]);
    } finally {
      setDoctorsLoading(false);
    }
  };

  const onDoctorSelect = async (doctor) => {
    setSelectedDoctor(doctor);
    setSelectedSlot(null);
    setSlots([]);
    setNoSlotsText("");
    await fetchSlotsForDoctor(doctor?.doctor_id, selectedDate);
  };

  const onDateChange = async (value) => {
    if (!value || !value.isValid()) return;
    setSelectedDate(value);
    if (selectedDoctor?.doctor_id) {
      await fetchSlotsForDoctor(selectedDoctor.doctor_id, value);
    }
  };

  const goToConfirm = () => {
    if (!selectedDoctor) {
      toast.error("Please select a doctor.");
      return;
    }
    if (!selectedSlot) {
      toast.error("Please select a slot.");
      return;
    }
    setStep("confirm");
  };

  const confirmAppointment = async () => {
    if (!selectedDoctor || !selectedSlot) return;
    setBlocking(true);
    try {
      const day = selectedDate.format("YYYY-MM-DD");
      const start24 = to24Hour(selectedSlot.slot_start_time);
      const end24 = to24Hour(selectedSlot.slot_end_time);

      if (!start24 || !end24) {
        throw new Error("Invalid slot timing selected.");
      }

      const payload = {
        doctor_id: selectedDoctor.doctor_id,
        appointment_start_time: `${day}T${start24}`,
        appointment_end_time: `${day}T${end24}`,
        appointment_mode: "ONLINE",
        new_patient: newPatientFlag,
        status: "SCHEDULED",
        shift: selectedSlot.shift || "first",
        slot_number: selectedSlot.slot_number,
      };

      const res = await webIntegrationAxios.post("add_appointment/", payload);
      if (res.data?.status !== "Success") {
        throw new Error(res.data?.message || "Could not book appointment.");
      }

      setAppointmentSummary({
        doctor: selectedDoctor,
        slot: selectedSlot,
        date: day,
        response: res.data,
      });
      setStep("booked");
      toast.success("Appointment booked successfully.");
    } catch (e) {
      toast.error(getMessage(e));
    } finally {
      setBlocking(false);
    }
  };

  const resetToRegistration = () => {
    setWebIntegrationAuthSnapshot({
      orgCode,
      appMode,
      accessToken: "",
      refreshToken: "",
    });
    setForm(initialForm);
    setPatientRefId("");
    setOtpDigits(["", "", "", "", "", ""]);
    setResendBusy(false);
    setPatientProfile(null);
    setNewPatientFlag(false);
    resetBookingState();
    setStep("registration");
  };

  return (
    <div className="wi-root" style={brandingPageStyle}>
      <Helmet>
        <title>{pageTitle}</title>
      </Helmet>

      {blocking && (
        <div className="wi-loader-overlay" aria-busy="true" aria-live="polite">
          <img
            className="wi-favicon-spin"
            src={`${process.env.PUBLIC_URL || ""}/favicon.ico`}
            alt=""
          />
          <span style={{ color: "#546e7a", fontSize: "0.95rem" }}>Loading...</span>
        </div>
      )}

      <div className={`wi-inner ${isBookingStage ? "wi-inner-wide" : ""}`}>
        <h1 className="wi-header-title">{pageTitle}</h1>

        {step === "invalid" && (
          <div className="wi-card">
            <p className="wi-error">{invalidReason}</p>
            <Button
              variant="contained"
              fullWidth
              className="wi-next-btn"
              onClick={() => runBootstrap()}
            >
              Retry
            </Button>
          </div>
        )}

        {step === "registration" && (
          <LocalizationProvider dateAdapter={AdapterDayjs}>
            <>
              {logoSrc ? (
                <img src={logoSrc} alt="Organization" className="wi-logo" />
              ) : null}

              <div className="wi-card">
                <h2
                  className="wi-heading"
                  style={{ fontSize: "1.2rem", marginBottom: "16px" }}
                >
                  Your details
                </h2>

                <div className="wi-field">
                  <label className="wi-label">Full Name *</label>
                  <TextField
                    fullWidth
                    size="small"
                    placeholder="Full Name"
                    value={form.name}
                    onChange={(e) => updateField("name", e.target.value)}
                  />
                </div>

                <div className="wi-field">
                  <label className="wi-label">Contact Number *</label>
                  <TextField
                    fullWidth
                    size="small"
                    placeholder="Contact Number"
                    value={form.phone}
                    inputProps={{
                      inputMode: "numeric",
                      maxLength: 10,
                      pattern: "[0-9]*",
                      "aria-label": "Mobile number",
                    }}
                    onChange={(e) => {
                      const digits = e.target.value
                        .replace(/\D/g, "")
                        .slice(0, 10);
                      updateField("phone", digits);
                    }}
                  />
                </div>

                <div className="wi-field">
                  <label className="wi-label">Email</label>
                  <TextField
                    fullWidth
                    size="small"
                    type="email"
                    placeholder="Email"
                    value={form.email}
                    onChange={(e) => updateField("email", e.target.value)}
                  />
                </div>

                <div className="wi-field">
                  <label className="wi-label">Date of Birth *</label>
                  <DatePicker
                    format="DD/MM/YYYY"
                    value={form.dob ? dayjs(form.dob) : null}
                    onChange={(v) =>
                      updateField(
                        "dob",
                        v && typeof v.isValid === "function" && v.isValid()
                          ? v.format("YYYY-MM-DD")
                          : ""
                      )
                    }
                    maxDate={dayjs()}
                    minDate={dayjs().subtract(120, "year")}
                    slotProps={{
                      textField: {
                        fullWidth: true,
                        size: "small",
                        placeholder: "dd/mm/yyyy",
                      },
                    }}
                  />
                </div>

                <div className="wi-field">
                  <label className="wi-label">Gender</label>
                  <FormControl fullWidth size="small">
                    <InputLabel id="wi-gender-label">Gender</InputLabel>
                    <Select
                      labelId="wi-gender-label"
                      label="Gender"
                      value={form.gender}
                      displayEmpty
                      onChange={(e) => updateField("gender", e.target.value)}
                    >
                      <MenuItem value="" sx={{ display: "none" }} />
                      <MenuItem value="M">Male</MenuItem>
                      <MenuItem value="F">Female</MenuItem>
                      <MenuItem value="O">Other</MenuItem>
                    </Select>
                  </FormControl>
                </div>

                <div className="wi-field">
                  <label className="wi-label">Address</label>
                  <TextField
                    fullWidth
                    size="small"
                    multiline
                    minRows={2}
                    placeholder="Address"
                    value={form.address}
                    onChange={(e) => updateField("address", e.target.value)}
                  />
                </div>

                <div className="wi-field">
                  <label className="wi-label">District</label>
                  <TextField
                    fullWidth
                    size="small"
                    placeholder="District"
                    value={form.district}
                    onChange={(e) => updateField("district", e.target.value)}
                  />
                </div>

                <div className="wi-field">
                  <label className="wi-label">State</label>
                  <TextField
                    fullWidth
                    size="small"
                    placeholder="State"
                    value={form.state}
                    onChange={(e) => updateField("state", e.target.value)}
                  />
                </div>

                <div className="wi-field">
                  <label className="wi-label">PIN code</label>
                  <TextField
                    fullWidth
                    size="small"
                    placeholder="PIN code"
                    value={form.pincode}
                    onChange={(e) => updateField("pincode", e.target.value)}
                  />
                </div>

                <Button
                  variant="contained"
                  color="primary"
                  fullWidth
                  className="wi-next-btn"
                  onClick={submitRegistration}
                  disabled={blocking}
                >
                  Next
                </Button>
              </div>
            </>
          </LocalizationProvider>
        )}

        {step === "otp" && (
          <>
            {logoSrc ? (
              <img src={logoSrc} alt="Organization" className="wi-logo" />
            ) : null}

            <h2 className="wi-heading">Verify your identity</h2>
            <p className="wi-sub">
              Secure and compliant verification for a seamless experience
            </p>

            <div className="wi-otp-card">
              <Box sx={{ fontWeight: 600, mb: 1.5, fontSize: "0.95rem" }}>
                Mobile OTP verification
              </Box>
              <div className="wi-otp-row">
                {otpDigits.map((d, i) => (
                  <input
                    key={i}
                    ref={(el) => {
                      otpInputRefs.current[i] = el;
                    }}
                    className="wi-otp-input"
                    inputMode="numeric"
                    maxLength={1}
                    value={d}
                    autoFocus={i === 0}
                    onChange={(e) => {
                      const v = e.target.value.replace(/\D/g, "").slice(-1);
                      const next = [...otpDigits];
                      next[i] = v;
                      setOtpDigits(next);
                      if (v && i < 5) {
                        otpInputRefs.current[i + 1]?.focus();
                      }
                    }}
                    onKeyDown={(e) => {
                      if (e.key === "Backspace" && !otpDigits[i] && i > 0) {
                        otpInputRefs.current[i - 1]?.focus();
                      }
                    }}
                  />
                ))}
              </div>
              <p style={{ marginTop: 12, fontSize: "0.875rem", color: "#546e7a" }}>
                Didn&apos;t receive code?{" "}
                <button
                  type="button"
                  className="wi-link-btn"
                  onClick={resendOtp}
                  disabled={resendBusy || blocking}
                >
                  Resend
                </button>
              </p>
            </div>

            <Button
              variant="contained"
              color="primary"
              fullWidth
              className="wi-next-btn"
              sx={{ mt: 2 }}
              onClick={verifyOtp}
              disabled={blocking || otpString.length < 4}
            >
              Next
            </Button>
          </>
        )}

        {step === "appointments" && (
          <LocalizationProvider dateAdapter={AdapterDayjs}>
            <div className="wi-card">
              {logoSrc ? (
                <img src={logoSrc} alt="Organization" className="wi-logo" />
              ) : null}

              <Autocomplete
                options={departments}
                loading={departmentLoading}
                value={selectedDepartment}
                onChange={onDepartmentChange}
                getOptionLabel={(option) => option?.department_name || ""}
                isOptionEqualToValue={(opt, val) =>
                  opt.department_id === val.department_id
                }
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label="Select department"
                    placeholder="Type to filter"
                    size="small"
                  />
                )}
              />

              <div className="wi-appointment-grid">
                <div className="wi-left-pane">
                  {doctorsLoading && <p className="wi-muted">Loading doctors...</p>}
                  {!doctorsLoading && selectedDepartment && doctors.length === 0 && (
                    <p className="wi-muted">
                      No doctors found for this department.
                    </p>
                  )}
                  <div className="wi-doctor-grid">
                    {doctors.map((doctor) => {
                      const selected = selectedDoctor?.doctor_id === doctor.doctor_id;
                      return (
                        <button
                          type="button"
                          key={doctor.doctor_id}
                          className={`wi-doctor-card ${selected ? "wi-doctor-card-active" : ""
                            }`}
                          onClick={() => onDoctorSelect(doctor)}
                        >
                          {doctor.photo ? (
                            <img
                              className="wi-doctor-avatar"
                              src={`data:image/png;base64,${doctor.photo}`}
                              alt={doctor.name}
                            />
                          ) : (
                            <div className="wi-doctor-avatar wi-doctor-avatar-fallback">
                              DR
                            </div>
                          )}
                          <div className="wi-doctor-name">
                            {doctor.salutation || "Dr."} {doctor.name}
                          </div>
                          <div className="wi-doctor-meta">
                            {doctor.specialization || doctor.department}
                          </div>
                          <div className="wi-doctor-meta">
                            {getLocationText(doctor.location)}
                          </div>
                          <div className="wi-doctor-meta">
                            Fee: {doctor.consultation_fees ?? "-"}
                          </div>
                        </button>
                      );
                    })}
                  </div>
                </div>

                <div className="wi-right-pane">
                  <DatePicker
                    value={selectedDate}
                    onChange={onDateChange}
                    format="DD/MM/YYYY"
                    minDate={dayjs()}
                    slotProps={{
                      textField: {
                        fullWidth: true,
                        size: "small",
                        label: "Select date",
                      },
                    }}
                  />

                  <div className="wi-slots-wrap">
                    <p className="wi-slot-title">Slots</p>
                    {slotsLoading && <p className="wi-muted">Loading slots...</p>}
                    {!slotsLoading && noSlotsText ? (
                      <p className="wi-muted">{noSlotsText}</p>
                    ) : null}
                    <div className="wi-slot-grid">
                      {slots.map((slot) => {
                        const isSelected =
                          selectedSlot?.slot_number === slot.slot_number &&
                          selectedSlot?.slot_start_time === slot.slot_start_time;
                        return (
                          <button
                            type="button"
                            key={`${slot.slot_number}-${slot.slot_start_time}`}
                            className={`wi-slot-btn ${isSelected ? "wi-slot-btn-active" : ""
                              }`}
                            onClick={() => setSelectedSlot(slot)}
                          >
                            {slot.slot_start_time}
                          </button>
                        );
                      })}
                    </div>
                  </div>

                  <Button
                    variant="contained"
                    className="wi-next-btn"
                    onClick={goToConfirm}
                    disabled={!selectedDoctor || !selectedSlot}
                  >
                    Book now
                  </Button>
                </div>
              </div>
            </div>
          </LocalizationProvider>
        )}

        {step === "confirm" && (
          <div className="wi-card wi-confirm-card">
            <h2 className="wi-heading wi-confirm-title">Confirm Appointment</h2>
            <p>
              <strong>Patient:</strong> {patientProfile?.name || form.name}
            </p>
            <p>
              <strong>Department:</strong>{" "}
              {selectedDepartment?.department_name || "-"}
            </p>
            <p>
              <strong>Doctor:</strong> {selectedDoctor?.salutation}{" "}
              {selectedDoctor?.name}
            </p>
            <p>
              <strong>Date:</strong> {selectedDate.format("DD/MM/YYYY")}
            </p>
            <p>
              <strong>Slot:</strong> {selectedSlot?.slot_start_time} -{" "}
              {selectedSlot?.slot_end_time}
            </p>

            <div className="wi-confirm-actions">
              <Button variant="outlined" onClick={() => setStep("appointments")}>
                Back
              </Button>
              <Button
                variant="contained"
                sx={containedButtonSx}
                onClick={confirmAppointment}
              >
                Confirm
              </Button>
            </div>
          </div>
        )}

        {step === "booked" && (
          <div className="wi-card wi-confirm-card">
            <div className="wi-success-icon-wrap">
              <CheckCircleIcon className="wi-success-icon" />
            </div>
            <h2 className="wi-heading wi-confirm-title">Appointment Booked</h2>
            <p>
              Your appointment with {appointmentSummary?.doctor?.salutation}{" "}
              {appointmentSummary?.doctor?.name} is confirmed for{" "}
              {dayjs(appointmentSummary?.date).format("DD/MM/YYYY")} at{" "}
              {appointmentSummary?.slot?.slot_start_time}.
            </p>
            <div className="wi-confirm-actions">
              <Button
                variant="contained"
                sx={containedButtonSx}
                onClick={resetToRegistration}
              >
                Book another appointment
              </Button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

/** Env reference for callers / future modules (matches project pattern) */
export function getWebIntegrationBaseEnv() {
  return REACT_APP_BACKEND_ROUTE_URL_WEB_INTEGRATION;
}

export default function WebsiteIntegration() {
  return <WebsiteIntegrationInner />;
}
